import { Injectable } from '@angular/core';
import { AuthService } from '@core/services/auth.service';
import { ComponentStore } from '@ngrx/component-store';
import { EMPTY, from, MonoTypeOperatorFunction, Observable, pipe } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

interface IChangePasswordState {
  passwordChanged: boolean;
  incorrectCurrentPassword: boolean;
  loading: boolean;
  error: string | null;
}

const initialState: IChangePasswordState = {
  error: null,
  incorrectCurrentPassword: false,
  loading: false,
  passwordChanged: false,
};

@Injectable()
export class ChangePasswordStore extends ComponentStore<IChangePasswordState> {
  public readonly passwordChanged$ = this.selectSignal((state: IChangePasswordState) => state.passwordChanged);
  public readonly incorrectCurrentPassword$ = this.selectSignal(
    (state: IChangePasswordState) => state.incorrectCurrentPassword,
  );
  public readonly loading$ = this.selectSignal((state: IChangePasswordState) => state.loading);
  public readonly error$ = this.selectSignal((state: IChangePasswordState) => state.error);

  private readonly handleError = (errorMessage: string): MonoTypeOperatorFunction<unknown> =>
    pipe(
      catchError(() => {
        this.patchState({
          error: errorMessage,
          loading: false,
        });

        return EMPTY;
      }),
    );

  public readonly changePassword = this.effect(
    (trigger$: Observable<{ password: string; currentPassword: string }>) => {
      return trigger$.pipe(
        tap(() => this.patchState({ error: null, loading: true, passwordChanged: false })),
        switchMap(({ password, currentPassword }) =>
          from(this.authService.signInWithEmail(AuthService.currentFirebaseUser?.email!, currentPassword)).pipe(
            catchError(() => {
              this.patchState({ incorrectCurrentPassword: true, loading: false });

              return EMPTY;
            }),
            switchMap(() => from(this.authService.updatePassword(password))),
            tap(() => this.patchState({ passwordChanged: true })),
            this.handleError('Failed to change password'),
          ),
        ),
      );
    },
  );

  constructor(private readonly authService: AuthService) {
    super(initialState);
  }
}
